import { colors } from '@kmns-internal/furikake'
import { DeleteOutlined, MoreHoriz } from '@mui/icons-material'
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  ExcelConversionDayOfWeekEnum,
  type ExcelConversionFlow,
  ExcelConversionScheduleFrequencyEnum,
  ExcelConversionScheduleStartupTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { type MouseEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import date from 'src/exShared/util/date'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import excelConversionFlowsOperations from 'src/state/ducks/excelConversionFlows/operations'

const useDispatchProps = () => {
  const dispatch = useDispatch()
  return {
    deleteFlow: (flowId: string) => {
      excelConversionFlowsOperations.deleteExcelConversionFlow(dispatch, flowId)
    },
  }
}

const getDayOfWeek = (dayOfWeek: ExcelConversionDayOfWeekEnum) => {
  switch (dayOfWeek) {
    case ExcelConversionDayOfWeekEnum.Monday:
      return '月'
    case ExcelConversionDayOfWeekEnum.Tuesday:
      return '火'
    case ExcelConversionDayOfWeekEnum.Wednesday:
      return '水'
    case ExcelConversionDayOfWeekEnum.Thursday:
      return '木'
    case ExcelConversionDayOfWeekEnum.Friday:
      return '金'
    case ExcelConversionDayOfWeekEnum.Saturday:
      return '土'
    case ExcelConversionDayOfWeekEnum.Sunday:
      return '日'
  }
}

const getMakeTime = (flow: ExcelConversionFlow) => {
  if (!(flow.schedules && flow.schedules[0])) {
    return ''
  }
  const schedule = flow.schedules[0]
  if (
    schedule.startupType ===
    ExcelConversionScheduleStartupTypeEnum.OnCompletedReport
  ) {
    return 'レポート提出時'
  }
  const startupTime = schedule.makeTime
    ? date.formatHHMM(schedule.makeTime)
    : ''
  switch (schedule.frequency) {
    case ExcelConversionScheduleFrequencyEnum.Daily: {
      return `毎日 ${startupTime}`
    }
    case ExcelConversionScheduleFrequencyEnum.Weekly: {
      if (!schedule.frequencyWeekly) {
        return ``
      }
      const frequencyWeekly = schedule.frequencyWeekly
      const dayOfWeek = frequencyWeekly.dayOfWeek
        ? getDayOfWeek(frequencyWeekly.dayOfWeek)
        : ''
      return `毎週 ${dayOfWeek}曜 ${startupTime}`
    }
    case ExcelConversionScheduleFrequencyEnum.Monthly: {
      if (!schedule.frequencyMonthly) {
        return ``
      }
      const frequencyMonthly = schedule.frequencyMonthly
      const dayOfMonth = frequencyMonthly.dayOfMonth
      return `毎月 ${dayOfMonth}日 ${startupTime}`
    }
  }
  return ''
}

const getFileName = (flow: ExcelConversionFlow) => {
  return flow.inputFiles?.[0] ? flow.inputFiles[0].name : ''
}

const ActiveChip = () => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Badge variant="dot" color="success" />
    <Typography variant="body2" color="success.main" fontWeight="bold">
      アクティブ
    </Typography>
  </Stack>
)

const InactiveChip = () => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Badge color="secondary" variant="dot" />
    <Typography variant="body2" color="text.secondary">
      停止中
    </Typography>
  </Stack>
)

const isActiveBadge = (flow: ExcelConversionFlow) => {
  if (flow.schedules && flow.schedules[0] && flow.schedules[0].isActive) {
    return <ActiveChip />
  } else {
    return <InactiveChip />
  }
}

type ExcelConversionFlowListProps = ExcelConversionFlowListContainerProps & {
  deleteFlow: (flowId: string) => void
}

interface RowData {
  uuid: string
  name?: string
}

const ExcelConversionFlowList: React.FC<ExcelConversionFlowListProps> = (
  props: ExcelConversionFlowListProps,
) => {
  const { isFeatureAvailable } = useLimitedFeatureAvailability()

  // 3点リーダーメニュー
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null)
  const handleMenuClick = (event: MouseEvent<HTMLElement>, row: RowData) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedRow(row)
  }
  const handleMenuClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  // 削除ダイアログ
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const handleMenuDelete = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setOpenDeleteDialog(true)
  }
  const handleCloseDeleteDialog = (event: MouseEvent<HTMLElement>) => {
    setOpenDeleteDialog(false)
    handleMenuClose(event)
  }
  const handleDelete = () => {
    if (selectedRow?.uuid !== undefined) {
      props.deleteFlow(selectedRow.uuid)
    }
    setOpenDeleteDialog(false)
    setAnchorEl(null)
  }

  return (
    <>
      <TableContainer>
        <Table sx={{ bgcolor: 'white', width: '100%' }} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell size="small" sx={{ width: '35%', fontWeight: 'bold' }}>
                変換フロー名
              </TableCell>
              <TableCell size="small" sx={{ width: '25%', fontWeight: 'bold' }}>
                作成タイミング
              </TableCell>
              <TableCell size="small" sx={{ width: '25%', fontWeight: 'bold' }}>
                変換対象ファイル名
              </TableCell>
              <TableCell size="small" sx={{ width: '15%', fontWeight: 'bold' }}>
                ステータス
              </TableCell>
              {isFeatureAvailable('excelConversionEdit') && (
                <TableCell size="small" sx={{ width: '60px' }} />
              )}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: 'white' }}>
            {props.flows.map((flow: ExcelConversionFlow) => (
              <TableRow
                key={flow.uuid}
                {...(isFeatureAvailable('excelConversionEdit')
                  ? {
                      component: Link,
                      to: `/excelConversionFlows/${flow.uuid}`,
                    }
                  : {})}
                sx={{
                  textDecoration: 'none',
                  cursor: isFeatureAvailable('excelConversionEdit')
                    ? 'pointer'
                    : 'default',
                  ...(isFeatureAvailable('excelConversionEdit')
                    ? {
                        '&:hover': {
                          backgroundColor: colors.action.hover,
                        },
                      }
                    : {}),
                }}
              >
                <TableCell size="small" sx={{ wordBreak: 'break-all' }}>
                  <Typography variant="body2">{flow.name}</Typography>
                </TableCell>
                <TableCell size="small" sx={{ wordBreak: 'break-all' }}>
                  <Typography variant="body2">{getMakeTime(flow)}</Typography>
                </TableCell>
                <TableCell size="small" sx={{ wordBreak: 'break-all' }}>
                  <Typography variant="body2">{getFileName(flow)}</Typography>
                </TableCell>
                <TableCell size="small">{isActiveBadge(flow)}</TableCell>
                {isFeatureAvailable('excelConversionEdit') && (
                  <TableCell
                    size="small"
                    align="right"
                    sx={{ minWidth: '60px' }}
                  >
                    <IconButton
                      onClick={e => {
                        e.stopPropagation()
                        e.preventDefault()
                        handleMenuClick(e, {
                          uuid: flow.uuid ?? '',
                          name: flow.name,
                        })
                      }}
                      aria-label="変換フローのメニューを開く"
                    >
                      <MoreHoriz />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={handleMenuDelete}
          sx={{
            color: colors.error.main,
            '&:hover': {
              backgroundColor: colors.error.hover,
              color: colors.error.main,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          {`「${selectedRow?.name}」の削除を実行しますがよろしいですか？`}
        </DialogTitle>
        <DialogActions>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleCloseDeleteDialog}
          >
            キャンセル
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

type ExcelConversionFlowListContainerProps = {
  flows: ExcelConversionFlow[]
}

const ExcelConversionFlowListContainer: React.FC<
  ExcelConversionFlowListContainerProps
> = _props => {
  const dispatchProps = useDispatchProps()
  const props = { ...dispatchProps, ..._props }
  return <ExcelConversionFlowList {...props} />
}

export default ExcelConversionFlowListContainer
